//? Library
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Container, Row, Col, Form, Button, Card, Nav, Badge } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import SummaryMoney from './SummaryMoney';
import SummarySection from './SummarySection';
import SummaryOrder from './SummaryOrder';
import SummaryStock from './SummaryStock';
import { useLazyQuery } from '@apollo/client';
import _ from 'lodash';
import { MdDateRange } from "react-icons/md";
import { FiFilter } from "react-icons/fi";
import { BsCashCoin, BsBoxSeam, BsCartCheck } from "react-icons/bs";

//? Function
import { COLOR_APP, K_LOCAL_STORAGE_PAGE_HISTORY, K_LOCAL_STORAGE_SHOP_DATA } from '../../constants';
import CurrentDateModal from '../../components/currentComponent/CurrentDateModal';
import { GET_PAGE_HISTORY } from '../../graphql/totalSummary/query';
import { convertCurrentDate } from "../../helpers";
import SelectMultiples from './SelectMultiples';
import SelectMultipleSystem from './SelectMultipleSystem';
import 'bootstrap/dist/css/bootstrap.min.css';

const TotalSummary = () => {
    const [systemsData, setSystemsData] = useState([]);
    const [pageHistoryData, setPageHistoryData] = useState(null);
    const [pages, setPages] = useState({ amount: 0, listName: '', listId: '' });
    const [systems, setSystems] = useState({ amount: 0, listName: '' });
    const [isCurrentModals, setIsCurrentModals] = useState(false);
    const [isLoading, setIsLoading] = useState({
        loadingSummaryMoney: false,
        loadingSummaryOrder: false,
        loadingSummaryStock: false
    });
    const [disabled, setDisabled] = useState(false);
    const [cdFilter, setCdFilter] = useState({
        createdAt_gte: "",
        createdAt_lt: "",
    });
    const [activeButton, setActiveButton] = useState("today");
    const [activeTab, setActiveTab] = useState("money");

    const hasFilter = systems?.amount > 0 || pages?.amount > 0 || !_.isEmpty(cdFilter?.createdAt_gte);

    const TABS = [
        { key: "money", label: "ຍອດເງີນ", icon: <BsCashCoin /> },
        { key: "order", label: "ຍອດອໍເດີ", icon: <BsCartCheck /> },
        { key: "stock", label: "ຍອດສີນຄ້າ", icon: <BsBoxSeam /> },
    ];

    const [getPageHistory] = useLazyQuery(
        GET_PAGE_HISTORY,
        { fetchPolicy: "cache-and-network" }
    );

    //* กันยิงซ้ำด้วย ref ไม่ใช่ state
    //* ถ้าใช้ loading จาก useLazyQuery เป็น dependency ของ useCallback
    //* identity ของ callback จะเปลี่ยนทุกครั้งที่ loading toggle -> useEffect rerun -> ยิงใหม่ วนไม่จบ
    const isFetchingPageHistory = useRef(false);

    // Improved fetchSystems with proper memoization
    const fetchSystems = useCallback(() => {
        try {
            const localShopData = localStorage.getItem(K_LOCAL_STORAGE_SHOP_DATA);
            if (localShopData) {
                const localData = JSON.parse(localShopData);
                const systems = localData?.systems || [];
                setSystemsData(systems);
            }
        } catch (error) {
            console.error("Error fetching systems:", error);
        }
    }, []); // Empty dependency array since it doesn't depend on any props or state

    // Improved fetchRemotePageHistory with proper memoization
    const fetchRemotePageHistory = useCallback(async () => {
        if (isFetchingPageHistory.current) return;
        isFetchingPageHistory.current = true;

        try {
            const newPageHistory = await getPageHistory({
                variables: {
                    where: {
                        save: true
                    }
                }
            });
            //* useLazyQuery ของ Apollo 3.7 ไม่ throw เวลา query พัง แต่คืน { data: undefined, error }
            //* ถ้าไม่เช็กตรงนี้ JSON.parse(undefined) จะระเบิดแล้วกลบ error ตัวจริงทิ้ง
            if (newPageHistory?.error) throw newPageHistory.error;

            const newPageHistoryData = newPageHistory?.data?.getPageHistory;
            if (!newPageHistoryData) {
                setPageHistoryData([]);
                return;
            }
            // save to local storage
            const expired = new Date();
            expired.setMinutes(expired.getMinutes() + 10);
            localStorage.setItem(K_LOCAL_STORAGE_PAGE_HISTORY, JSON.stringify({ expired, data: newPageHistoryData }));
            setPageHistoryData(JSON.parse(newPageHistoryData));
        } catch (error) {
            console.error("Error fetching remote page history:", error);
        } finally {
            isFetchingPageHistory.current = false;
        }
    }, [getPageHistory]);

    // Improved fetchPageHistory with proper memoization
    const fetchPageHistory = useCallback(async () => {
        try {
            const localPageHistory = localStorage.getItem(K_LOCAL_STORAGE_PAGE_HISTORY);
            
            if (localPageHistory) {
                const localData = JSON.parse(localPageHistory);
                const expired = new Date(localData?.expired);
                const now = new Date();
                
                if (localData?.data && expired > now) {
                    // console.log("local");
                    const newPageHistory = JSON.parse(localData?.data);
                    setPageHistoryData(newPageHistory);
                } else {
                    // console.log("api");
                    await fetchRemotePageHistory();
                }
            } else {
                await fetchRemotePageHistory();
            }
        } catch (error) {
            console.error("Error fetching page history:", error);
        }
    }, [fetchRemotePageHistory, setPageHistoryData]);

    //* โหลด systems + page history ครั้งเดียวตอน mount
    //* เดิมรวมอยู่ใน effect เดียวกับ handleLoading ทำให้ทุกครั้งที่ลูกเรียก setIsLoading
    //* effect จะ rerun แล้วยิง getPageHistory ใหม่โดยไม่จำเป็น
    const didInit = useRef(false);
    useEffect(() => {
        if (didInit.current) return;
        didInit.current = true;
        fetchSystems();
        fetchPageHistory();
    }, [fetchSystems, fetchPageHistory]);

    useEffect(() => {
        setDisabled(
            isLoading.loadingSummaryMoney ||
            isLoading.loadingSummaryOrder ||
            isLoading.loadingSummaryStock
        );
    }, [isLoading]);

    // console.log("loading: ",isLoading.loadingSummaryMoney ,isLoading.loadingSummaryOrder ,isLoading.loadingSummaryStock, "disabled: ", disabled);

    // if (loadingPageHistory) return <p>Loading Filter...</p>

    return (
        <Container fluid className="pb-5">
            <div className="d-flex align-items-center justify-content-between mt-4 mb-4">
                <div>
                    <h3 style={{ fontWeight: 700, margin: 0 }}>ສະຫລຸບຍອດ</h3>
                    <span className="text-muted">ພາບລວມຍອດຂາຍ, ອໍເດີ ແລະ ສີນຄ້າ</span>
                </div>
                {disabled && <Badge bg="secondary" className="px-3 py-2">ກໍາລັງໂຫຼດຂໍ້ມູນ...</Badge>}
            </div>

            {/* Filter bar */}
            <Card className="mb-4 border-0" style={{ borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                <Card.Body className="p-4">
                    <Row className="g-3 align-items-end">
                        <Col xs={12} md={4}>
                            <Form.Group>
                                <Form.Label className="text-muted small mb-1">ລະບົບ</Form.Label>
                                <div>{!_.isEmpty(systemsData) && <SelectMultipleSystem setOption={setSystems} options={systemsData} isLoading={isLoading} />}</div>
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Group>
                                <Form.Label className="text-muted small mb-1">ເພສ</Form.Label>
                                <div>{!_.isEmpty(pageHistoryData) && <SelectMultiples setOption={setPages} options={pageHistoryData} isLoading={isLoading} />}</div>
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Group>
                                <Form.Label className="text-muted small mb-1">ວັນທີ,ເດືອນ,ປີ</Form.Label>
                                <Button
                                    disabled={disabled}
                                    variant="outline-secondary"
                                    onClick={() => setIsCurrentModals(true)}
                                    style={{ width: "100%", display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}
                                >
                                    <MdDateRange style={{ fontSize: 20 }} />
                                    {cdFilter?.createdAt_gte === "" && cdFilter?.createdAt_lt === "" ? (
                                        "mm/dd/yyyy"
                                    ) : (
                                        <>{convertCurrentDate(cdFilter?.createdAt_gte) + " - " + convertCurrentDate(cdFilter?.createdAt_lt)}</>
                                    )}
                                </Button>
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* modal current date  */}
                    <CurrentDateModal
                        onCloseModal={() => setIsCurrentModals(false)}
                        openModal={isCurrentModals}
                        setCdFillter={setCdFilter}
                        setActiveButton={setActiveButton}
                        activeButton={activeButton}
                        backdrop={true}
                    />

                    {hasFilter && (
                        <div className="mt-4 pt-4 border-top">
                            <SummarySection systems={systems} pages={pages} date={cdFilter} />
                        </div>
                    )}
                </Card.Body>
            </Card>

            {!hasFilter ? (
                <Card className="text-center py-5 border-0" style={{ borderRadius: 12, background: '#f8f9fb' }}>
                    <Card.Body>
                        <FiFilter size={36} className="text-muted mb-3" />
                        <h5 className="text-muted mb-0">ກະລຸນາເລືອກລະບົບ, ເພສ ຫລື ວັນທີ ເພື່ອເບິ່ງຍອດສະຫລຸບ</h5>
                    </Card.Body>
                </Card>
            ) : (
                <Card className="border-0" style={{ borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                    <Card.Body className="p-4">
                        <Nav className="mb-4 gap-2" style={{ borderBottom: '1px solid #eee' }}>
                            {TABS.map((tab) => (
                                <Nav.Item key={tab.key}>
                                    <Nav.Link
                                        active={activeTab === tab.key}
                                        onClick={() => setActiveTab(tab.key)}
                                        className="d-flex align-items-center gap-2 px-3 pb-3"
                                        style={activeTab === tab.key
                                            ? { fontWeight: 600, color: COLOR_APP, borderBottom: `3px solid ${COLOR_APP}`, borderRadius: 0 }
                                            : { color: '#6c757d', borderBottom: '3px solid transparent', borderRadius: 0 }
                                        }
                                    >
                                        {tab.icon} {tab.label}
                                    </Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>
                        {activeTab === "money" && (
                            <SummaryMoney systems={systems} pages={pages} date={cdFilter} setIsLoading={setIsLoading} />
                        )}
                        {activeTab === "order" && (
                            <SummaryOrder systems={systems} pages={pages} date={cdFilter} setIsLoading={setIsLoading} />
                        )}
                        {activeTab === "stock" && (
                            <SummaryStock systems={systems} pages={pages} date={cdFilter} setIsLoading={setIsLoading} />
                        )}
                    </Card.Body>
                </Card>
            )}
        </Container>
    );
};

export default TotalSummary;